import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Popconfirm, Tooltip, message, notification } from "antd";
import { EyeOutlined, SendOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { RowUpdatedEvent } from 'devextreme/ui/data_grid';

import { IUseTableReadyToSend } from "./IUseTableReadyToSend";
import { ContractDTO, SharesType } from "../../Models";

export const useTableReadyToSend = ({ contractAPI, signaturitAPI, investorAPI, translations, moduleRole }: IUseTableReadyToSend) => {
    const [loading, setLoading] = useState<boolean>(false);
	const [contractListData, setContractListData] = useState<ContractDTO[]>([]);
    const [sharesTypes, setSharesTypes] = useState<SharesType[]>([]);
	const [canSend, setCanSend] = useState<boolean>(true);
	const [pendingContractIds, setPendingContractIds] = useState<number[]>([]);
	const isReadOnly = moduleRole === "Read";

	const navigate = useNavigate();

    useEffect(() => {
		getInRevisionContracts();
		getSharesTypes();
    }, []);
    
	useEffect(() => {
		setLoading(false);
	}, [contractListData]);

    const getSharesTypes = () => {
		contractAPI.getShareTypes()
			.then((data: SharesType[]) => setSharesTypes(data))
	};

	const getInRevisionContracts = () => (
		contractAPI.getInRevisionContracts().then((res:any) => {
			if(res.success){
				let data = res.data.map((contract: ContractDTO) => {
					contract.signatureDate = contract.signatureDate?.includes('0001-01-01') ? undefined : contract.signatureDate
					return contract
				});
				setContractListData(data);
			}
		})
	);

	const deleteContract = (contractId: number) => (
        contractAPI.deleteContract(contractId).then((res: any) => {
			if(res.success){
				notification.success({ message: translations.contractDeleted });
				getInRevisionContracts()
			}
			else{
				notification.error({ message: translations.contractNotDeleted });
			}
		})
	);

	const editarContrato = (contractId: number, isTransfer: boolean) => {
        sessionStorage.isEditing = (!isReadOnly).toString();
        sessionStorage.isCreating = false;
        if(!isTransfer) {
        	navigate(`/FormContract?ContractId=${contractId}`);
		} else {
			navigate(`/TransferContract?ContractId=${contractId}`);
		}
	};

	const cellRenderEditIcon = (row: any) => {
	  return (
		  <div>
			  {<Tooltip placement="top" title={isReadOnly ? translations.access : translations.edit}>
				  <button className="btnDelete" disabled={!isReadOnly && !canSend} onClick={() => editarContrato(row.data.id, row.data.isTransfer)} > 
					  {isReadOnly ? <EyeOutlined /> : <EditOutlined type="edit" />}
				  </button>
			  </Tooltip> }
		  </div>
	  );
  };

	const cellRenderCancelIcon = (row: any) => {
		if (isReadOnly) {
			return <></>;
		}

		return	<Popconfirm
				placement="left"
				title={translations.sureDeleteContract}
				onConfirm={() => deleteContract(row.data.id)}
				onCancel={() => console.log('cancelado')}
				okText={translations.si}
				cancelText={translations.no}
				disabled={!canSend} 
			>
				<button className="btnDelete" disabled={!canSend} > 
					<DeleteOutlined />
				</button>
			</Popconfirm>
    };


	const markContractAsPending = (id: number) => {
		setPendingContractIds((current) => current.includes(id) ? current : [...current, id]);
	};

	const clearPendingContract = (id: number) => {
		setPendingContractIds((current) => current.filter((contractId) => contractId !== id));
	};

	const scheduleContractsRefresh = (id: number) => {
		setTimeout(() => {
			getInRevisionContracts().finally(() => clearPendingContract(id));
		}, 2000);
	};

	const createSignature = (id: number) => {
		markContractAsPending(id);
		signaturitAPI.createSignature(id)
			.then((res: any) => {
				if (res?.data?.success) {
					message.success(`Contract with id ${id} is being generated`);
				} else {
					notification.error({ message: `There was an error generating contract with id ${id}` });
					clearPendingContract(id);
				}
			})
			.catch(() => {
				notification.error({ message: `There was an error generating contract with id ${id}` });
				clearPendingContract(id);
			});

		scheduleContractsRefresh(id);
	}

	const sendContract = (id: number) => {
		markContractAsPending(id);
		signaturitAPI.sendContract(id)
			.then((res: any) => {
				if (res?.data?.success) {
					message.success(`Contract with id ${id} is being sent`);
				} else {
					notification.error({ message: `There was an error sending contract with id ${id}` });
					clearPendingContract(id);
				}
			})
			.catch(() => {
				notification.error({ message: `There was an error sending contract with id ${id}` });
				clearPendingContract(id);
			});

		scheduleContractsRefresh(id);
	}

    
    const cellRenderSendIcon = (row: any) => {
		if (isReadOnly) {
			return <></>;
		}

		let canSendContract = row.data.idShareType;
		const isPending = pendingContractIds.includes(row.data.id);
		if(row.data.idContractState === 2){
			return (
				<Popconfirm 
					placement="left"
					title={translations.sureSendContract}
					okText={translations.send}
					cancelText={translations.cancel}
					disabled={!canSendContract || isPending} 
					onConfirm={() => createSignature(row.data.id)}>
						<button className="send-btn" disabled={!canSendContract || isPending} ><SendOutlined /> {translations.enviar} </button>
				</Popconfirm>
			)
		}
		if(row.data.idContractState === 8){
			return (
				<Popconfirm 
					placement="left"
					title={translations.sureOmitWaiting}
					okText={translations.send}
					cancelText={translations.cancel}
					disabled={!canSendContract || isPending} 
					onConfirm={() => sendContract(row.data.id)}>
						<button className="send-btn" disabled={!canSendContract || isPending} ><SendOutlined /> {translations.omitirEspera} </button>
				</Popconfirm>
			)
		}

		return <></>;
	};
			
    const getFilteredSharesTypes = (data: ContractDTO) => {
		if (sharesTypes.filter((x: any) => x.sharesType === "Select...").length === 0) {
			sharesTypes.push({
				idSharesType: 0,
				sharesType: "Select...",
				idVehicle: 0,
				isin: "string",
				active: true,
				refDate: "",
				minRange: 0,
				maxRange: 0,
				days: 0,
				idPlacementAgent: 0,
				priority: "",
				SubscriptionFee: false
			});
		}
		if (data) {
			return sharesTypes.filter((x: any) => x.idVehicle === data.vehicleId || x.sharesType === "Select...");
		} else {
			return sharesTypes;
		}
	};

    const updateContract = async(e: RowUpdatedEvent) => {
		e.data.signatureDate = e.data.signatureDate ?? '0001-01-01' 
		delete e.data.personType;
		if(e.data.idShareType){
			await investorAPI.updateShareType(e.data.id, e.data.idShareType)
				.then((res: any) => {
					if (res.success) {
						message.success(`Shares type for contract with id ${e.data.id} updated` );
					} else {
						message.error(`There was an error saving Shares type for contract with id ${e.data.id}`);
					}
				});
		}
		if (e.data.signatureProvider) {
			await contractAPI.updateContract(e.data)
				.then((res: any) => {
					if (res.success) {
						message.success(`Contract with id ${e.data.id} updated`);
					} else {
						message.error(`There was an error saving Contract with id ${e.data.id}`);
					}
				});
		}
		getInRevisionContracts()
	};

	const checkDate = (date: any) => {
		const format = /^\d{4}-\d{2}-\d{2}$/;
		if (!format.test(date)) {
		  return false;
		}
		const [year, month, day] = date.split('-').map(Number);  
		return(
			year >= 1900 &&
			year <= 9999 &&
			month >= 1 &&
			month <= 12 &&
			day >= 1
		)
	};

	const onRowValidating = (e: any) => {
		if (isReadOnly) {
			e.isValid = false;
			return;
		}

		if(e.newData.signatureDate){
			let isValid = checkDate(e.newData.signatureDate)
			e.isValid = isValid;

			if(!isValid){
				notification.error({ message:  `Please use format 'YYYY-MM-DD' for date ${e.newData.signatureDate}` });
			}else{
				setCanSend(true)
			}
		}
	};

	const cellRenderDate = (date: any) => {
		if(!date || date.includes('0001-01-01')){
			return <p className="datePlaceholder">YYYY-MM-DD</p>
		}else{
			return moment(date).format('YYYY-MM-DD');
		}
	};

	const cellRenderSendDate = (date: any, state: any) => {
		if(state === 8 && date){
			return moment(date).format('DD/MM/YYYY HH:mm');
		}

		return null;
	};

	const onCellPrepared = (e: any) => {
		if (e.rowType === 'data') {
			const { idContractState } = e.data;	
			if (idContractState === 8) {
				e.cellElement.style.color = 'rgb(255, 131, 26)';
			}
		}
	};


    return{
        contractListData,
        cellRenderSendIcon,
        loading,
        getFilteredSharesTypes,
        updateContract,
		onRowValidating,
		cellRenderDate,
		cellRenderCancelIcon,
		cellRenderEditIcon,
		setCanSend,
		onCellPrepared,
		cellRenderSendDate
    };
};
